import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getColors, fonts } from '../theme';
import { PageHead, Section, Row } from '../components/Notebook';

const STEPS = [
  { title: 'Choose one task', description: "Pick a single thing to work on. Don't multitask." },
  { title: 'Work for 25 minutes', description: 'Start the timer and give it your full attention.' },
  { title: 'Take a 5-minute break', description: 'Step away. Stretch, drink water, rest your eyes.' },
  { title: 'Repeat four times', description: 'After four pomodoros, take a longer 15–30 minute break.' },
];

const SCIENCE = [
  { title: 'Attention span', description: 'Focused attention holds for roughly 25–50 minutes before performance drops. The technique works with that rhythm.' },
  { title: 'Memory', description: 'Short breaks give the brain time to move what you just worked on into long-term memory, which is why ideas often land on a break.' },
  { title: 'Reward', description: 'Finishing a pomodoro is a small win. Over time, focused work starts to feel rewarding in itself.' },
  { title: 'Urgency', description: "Work expands to fill the time available (Parkinson's law). A 25-minute limit gets you into flow faster." },
];

const PRACTICES = [
  { title: 'Remove distractions', description: "Turn on Do Not Disturb, close extra tabs, and tell people you're focusing." },
  { title: 'Count your pomodoros', description: "Note how many each task takes. You'll get better at estimating work." },
  { title: 'One task per pomodoro', description: "Don't switch mid-session. Write new ideas down and come back to them." },
  { title: 'Protect your breaks', description: 'Get up from your desk. A break spent on email is not a break.' },
  { title: 'Adjust the intervals', description: 'Some work suits longer sessions, like 50/10. Find your own rhythm.' },
  { title: 'Start with the hardest task', description: 'Willpower is highest early in the day. Spend it on the difficult thing.' },
];

const IN_MIND = [
  { title: 'Focus on a task', description: 'Press the timer button on any task to attach it to the session.' },
  { title: 'Your own durations', description: 'Set focus and break lengths in the timer settings.' },
  { title: 'Notifications', description: "Get a sound or a browser notification when it's time to switch." },
  { title: 'Session count', description: 'See how many sessions you have finished and how many minutes you focused today.' },
  { title: 'Always with you', description: 'The timer lives in the header and keeps running as you move between pages.' },
];

const PomodoroExplainerPage = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);

  const numbered = (items) => items.map((item, i) => (
    <Row key={item.title} margin={String(i + 1).padStart(2, '0')} mark={<span className="font-mono">•</span>}>
      <span className="block font-medium">{item.title}</span>
      <span className="block text-sm mt-0.5" style={{ color: colors.textSecondary }}>{item.description}</span>
    </Row>
  ));

  const prose = 'mt-4 leading-relaxed max-w-[62ch]';

  return (
    <div className="max-w-3xl mx-auto px-4 pb-16">
      <PageHead size="lg" meta="Focus timer" title="The Pomodoro Technique">
        <p className="mt-5 text-[17px] leading-relaxed max-w-[56ch]" style={{ color: colors.textSecondary }}>
          Work in focused intervals, traditionally 25 minutes, separated by short breaks. Each interval
          is called a pomodoro, Italian for tomato.
        </p>
      </PageHead>

      <Section title="How it works" aside="4 steps">
        {numbered(STEPS)}
      </Section>

      <Section title="Why it works">
        {numbered(SCIENCE)}
      </Section>

      <Section title="Where it comes from">
        <p className={prose} style={{ color: colors.textSecondary }}>
          Francesco Cirillo invented the technique in the late 1980s as a university student who was
          struggling to focus. He challenged himself to study for ten focused minutes, timed with a
          tomato-shaped kitchen timer, and kept experimenting from there.
        </p>
        <p className={prose} style={{ color: colors.textSecondary }}>
          He settled on 25 minutes as the interval that worked for most people and formalized the
          method in 1992. It is now one of the most widely used ways to structure focused work.
        </p>
        <blockquote className="mt-6 pl-4" style={{ borderLeft: `1px solid ${colors.textPrimary}` }}>
          <p className="text-[17px] italic leading-relaxed" style={{ fontFamily: fonts.serif, color: colors.textPrimary }}>
            "I discovered that you could learn how to improve your effectiveness and be better able to estimate
            how long a task will take to complete by recording how you utilize your time."
          </p>
          <footer className="font-mono text-xs mt-2" style={{ color: colors.textSecondary }}>Francesco Cirillo</footer>
        </blockquote>
      </Section>

      <Section title="Best practices" aside={`${PRACTICES.length} habits`}>
        {numbered(PRACTICES)}
      </Section>

      <Section title="In mind.">
        {numbered(IN_MIND)}
      </Section>

      <Section title="Further reading">
        {[
          { href: 'https://francescocirillo.com/pages/pomodoro-technique', label: 'The Pomodoro Technique', source: 'Francesco Cirillo' },
          { href: 'https://todoist.com/productivity-methods/pomodoro-technique', label: 'A guide to the Pomodoro Technique', source: 'Todoist' },
        ].map(link => (
          <Row
            key={link.href}
            margin="↗"
            mark={null}
            meta={<span className="font-mono text-xs" style={{ color: colors.textSecondary }}>{link.source}</span>}
          >
            <a href={link.href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:opacity-70">
              {link.label}
            </a>
          </Row>
        ))}
      </Section>

      <div className="mt-12 flex flex-wrap items-center gap-5">
        <Link
          to="/tasks"
          className="inline-flex items-center min-h-[44px] px-6 text-sm font-medium transition-opacity hover:opacity-85"
          style={{ backgroundColor: colors.textPrimary, color: colors.onAccent }}
        >
          Open the focus timer
        </Link>
        <Link to="/" className="font-mono text-xs underline underline-offset-4" style={{ color: colors.textSecondary }}>
          Back to Today
        </Link>
      </div>
    </div>
  );
};

export default PomodoroExplainerPage;
